// ONE ASSET PICKER: the three the desk uses all day as chips, the rest behind
// a dropdown. Operator, 12 Sept 2026, on a fourteen-line select: "Not so many,
// only most used 3, like INR / USDT TRC & AED cash, rest under drop down."
//
// The chosen asset is always visible as a chip, even when it came from the
// dropdown, so the screen never shows a selection the operator cannot see.
import { displayAsset } from '../lib/format';
import { foldAsset } from '../lib/naming';
import { ASSETS } from '../lib/workflow';
import { Chip } from './FilterRail';

/** The desk's everyday three, in the order the operator said them. */
export const QUICK_ASSETS: readonly string[] = ['INR', 'USDTTRC', 'AED'];

export function AssetPicker({ value, onChange, options, quick, exclude, allowEmpty }: {
  value: string;
  onChange: (asset: string) => void;
  /** The assets on offer. Defaults to the registry; pass folded codes (USDT)
   *  on screens that deal in party boxes. */
  options?: readonly string[];
  /** The chips. Defaults to the desk's three, folded to match `options`. */
  quick?: readonly string[];
  /** Assets that cannot be picked here (the other side of a pair). */
  exclude?: (asset: string) => boolean;
  /** Show "pick asset" in the dropdown when nothing is chosen (the default). */
  allowEmpty?: boolean;
}) {
  const all = (options ?? ASSETS).filter((a) => !(exclude?.(a)));
  const folded = all.every((a) => a === foldAsset(a));
  const wanted = (quick ?? QUICK_ASSETS).map((q) => (folded ? foldAsset(q) : q));
  const chips = wanted.filter((q) => all.includes(q));
  if (value && !chips.includes(value) && all.includes(value)) chips.push(value);
  // THE DROPDOWN LISTS EVERYTHING, chips included. With only the tail in it,
  // an operator who reached for "pick asset" found no AED there and read the
  // chips as labels, not buttons ("nothing to pick aed", 12 Sept 2026). Two
  // ways to the same choice; the chips are the shortcut.
  const rest = all;
  // 21 Oct 2026 audit: with the value lit as a chip, the dropdown beside it read the
  // same asset again ("INR · USDT · [USDT v]"). The closed box now shows a neutral
  // "More…" whenever a chip is lit, and keeps "pick asset…" while nothing is chosen.
  // The LIST is unchanged — everything, chips included (12 Sept ruling above).
  const chipLit = !!value && chips.includes(value);
  return (
    <div className="flex flex-wrap items-center gap-1.5 lg:gap-1">
      {chips.map((a) => (
        <Chip key={a} active={a === value} onClick={() => onChange(a)}>{displayAsset(a)}</Chip>
      ))}
      {rest.length > 0 && (
        <select
          className="select py-1.5 lg:py-1 lg:text-meta w-auto"
          value=""
          onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
          aria-label="other assets"
        >
          <option value="">{chipLit ? 'More…' : allowEmpty !== false && !value ? 'pick asset…' : 'change…'}</option>
          {rest.map((a) => <option key={a} value={a}>{displayAsset(a)}</option>)}
        </select>
      )}
    </div>
  );
}
